import React from 'react';
import {
  MicroreelGroup,
  getGroupReels,
} from '../../../../data/models/Microreels/MicroreelGroup';
import Tooltip from '../../../../uiToolkit/Tooltip';
import ImageWithFallback from '../../../../uiToolkit/ImageWithFallback/ImageWithFallback';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import {microreelsListText} from '../microreelsListText';
import {formatTimeAgo} from '../../../../utils/notificationTimeTextConvertor';
import MicroreelCardOptions from './MicroreelCardOptions';
import styles from './MicroreelGroupCard.module.css';

interface Props {
  group: MicroreelGroup;
  onOpen: (group: MicroreelGroup) => void;
  onCourseEditor: (group: MicroreelGroup) => void;
  onEditInfo: (group: MicroreelGroup) => void;
  onDelete: (group: MicroreelGroup) => void;
  onLearnerProgress: (group: MicroreelGroup) => void;
  onMoveToProject?: (group: MicroreelGroup) => void;
  hideProject?: boolean;
}

const GHOST_DEPTH_CLASS = [styles.ghostOne, styles.ghostTwo, styles.ghostThree];

const MicroreelGroupCard = React.memo(
  ({
    group,
    onOpen,
    onCourseEditor,
    onEditInfo,
    onDelete,
    onLearnerProgress,
    onMoveToProject,
    hideProject,
  }: Props) => {
    const reels = getGroupReels(group);
    const poster = group.image || reels[0]?.poster;
    const timeAgo = formatTimeAgo(group.dateUpdated);
    const countLabel = microreelsListText.reelsCount(reels.length);
    const canOpenLearners = !!group.publishedCourseId && !!group.enrollNumber;

    const learnersBadgeContent = (
      <>
        <Icon
          name="workspace_temporal_learners.svg"
          className={styles.learnersIcon}
        />
        {group.enrollNumber || microreelsListText.notShared}
      </>
    );

    const ghostPosters = reels.length
      ? [0, 1, 2].map(
          index => reels[(index + 1) % reels.length]?.poster || poster,
        )
      : [];

    return (
      <div
        className={`${styles.cardContainer} ${
          reels.length > 1 ? styles.stacked : ''
        }`}>
        {reels.length > 1 &&
          ghostPosters.map((ghostPoster, index) => (
            <span
              key={index}
              className={`${styles.ghost} ${GHOST_DEPTH_CLASS[index]}`}
              style={
                ghostPoster
                  ? {backgroundImage: `url(${ghostPoster})`}
                  : undefined
              }
            />
          ))}
        <div className={styles.card} onClick={() => onOpen(group)}>
          <div className={styles.imageContainer}>
            {poster ? (
              <ImageWithFallback src={poster} className={styles.image} />
            ) : (
              <Icon name="microreel.svg" className={styles.fallbackIcon} />
            )}
            <span className={styles.countChip}>{countLabel}</span>
          </div>
          <div className={styles.titleContainer}>
            {!hideProject && group.projectName && (
              <div className={styles.projectRow}>
                <Icon name="projects-menu.svg" className={styles.projectIcon} />
                <span>{group.projectName}</span>
              </div>
            )}
            <Tooltip tooltipText={group.title}>
              <p>{group.title}</p>
            </Tooltip>
            <div className={styles.metaRow}>
              {timeAgo && <span>{microreelsListText.updatedAgo(timeAgo)}</span>}
              {canOpenLearners ? (
                <button
                  className={`${styles.learnersBadge} ${styles.learnersBadgeButton}`}
                  title={microreelsListText.viewLearnerProgress}
                  onClick={ev => {
                    ev.stopPropagation();
                    onLearnerProgress(group);
                  }}>
                  {learnersBadgeContent}
                </button>
              ) : (
                <div
                  className={`${styles.learnersBadge} ${
                    group.enrollNumber ? '' : styles.learnersBadgeEmpty
                  }`}>
                  {learnersBadgeContent}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={styles.optionsPosition}
          onClick={ev => ev.stopPropagation()}>
          <MicroreelCardOptions
            options={[
              {label: microreelsListText.open, onClick: () => onOpen(group)},
              {
                label: microreelsListText.courseEditor,
                onClick: () => onCourseEditor(group),
              },
              {
                label: microreelsListText.editInfo,
                onClick: () => onEditInfo(group),
              },
              ...(onMoveToProject
                ? [
                    {
                      label: microreelsListText.moveToProject,
                      onClick: () => onMoveToProject(group),
                    },
                  ]
                : []),
              {
                label: microreelsListText.delete,
                onClick: () => onDelete(group),
                danger: true,
              },
            ]}
          />
        </div>
      </div>
    );
  },
);

export default MicroreelGroupCard;
